import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, User, Shield, ShieldCheck, KeyRound, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { api, auth } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const roleIcons = {
  super_admin: ShieldCheck,
  admin: Shield,
  caseworker: User,
  // Backwards compatibility for existing users
  user: User,
};

const roleColors = {
  super_admin: "bg-purple-50 text-purple-700 border-purple-200",
  admin: "bg-blue-50 text-blue-700 border-blue-200",
  caseworker: "bg-slate-50 text-slate-700 border-slate-200",
  // Backwards compatibility
  user: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "caseworker",
  });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [userToEditEmail, setUserToEditEmail] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const currentUser = auth.getUser();
  const isSuperAdmin = currentUser?.role === "super_admin";

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.listUsers();
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to load users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setCreating(true);
      await api.createUser(newUser);
      toast.success("User created successfully");
      setDialogOpen(false);
      setNewUser({ email: "", password: "", name: "", role: "caseworker" });
      loadUsers();
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to delete ${userName}?`)) {
      return;
    }

    try {
      await api.deleteUser(userId);
      toast.success("User deleted");
      loadUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openResetDialog = (user) => {
    setUserToReset(user);
    setResetPassword("");
    setOtpRequired(false);
    setOtpCode("");
    setResetDialogOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPassword) return;

    try {
      await api.updateUserPassword(userToReset.id, { password: resetPassword, otp_code: otpCode || undefined });
      toast.success(`Password reset for ${userToReset.name}`);
      setResetDialogOpen(false);
      setUserToReset(null);
      setOtpRequired(false);
      setOtpCode("");
    } catch (error) {
      if (error.response?.data?.detail === "OTP_REQUIRED") {
        setOtpRequired(true);
        toast.info("A verification code has been sent to your email.");
      } else {
        console.error("Failed to reset password:", error);
        toast.error(error.response?.data?.detail || "Failed to reset password");
      }
    }
  };

  const openEmailDialog = (user) => {
    setUserToEditEmail(user);
    setNewEmail(user.email);
    setOtpRequired(false);
    setOtpCode("");
    setEmailDialogOpen(true);
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!newEmail) return;

    try {
      await api.updateUserEmail(userToEditEmail.id, { email: newEmail, otp_code: otpCode || undefined });
      toast.success(`Email updated for ${userToEditEmail.name}`);
      setEmailDialogOpen(false);
      setUserToEditEmail(null);
      setOtpRequired(false);
      setOtpCode("");
      loadUsers();
    } catch (error) {
      if (error.response?.data?.detail === "OTP_REQUIRED") {
        setOtpRequired(true);
        toast.info("A verification code has been sent to your email.");
      } else {
        console.error("Failed to update email:", error);
        toast.error(error.response?.data?.detail || "Failed to update email");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" data-testid="back-to-dashboard">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900 font-['Manrope']" data-testid="users-title">
                  Case Worker Management
                </h1>
                <p className="text-sm text-slate-500">Manage system case workers and permissions</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-slate-700 hover:bg-slate-800 rounded-sm"
                    data-testid="add-user-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Case Worker
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-['Manrope']">Create New Case Worker</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        placeholder="Full name"
                        className="mt-1.5 h-10 rounded-sm"
                        data-testid="new-user-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="email@example.com"
                        className="mt-1.5 h-10 rounded-sm"
                        data-testid="new-user-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="password">Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        placeholder="Set password"
                        className="mt-1.5 h-10 rounded-sm"
                        data-testid="new-user-password"
                      />
                    </div>
                    <div>
                      <Label htmlFor="role">Role</Label>
                      <Select
                        value={newUser.role}
                        onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                      >
                        <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="new-user-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="caseworker">Case Worker</SelectItem>
                          {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setDialogOpen(false)}
                        className="rounded-sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={creating}
                        className="bg-slate-700 hover:bg-slate-800 rounded-sm"
                        data-testid="create-user-submit"
                      >
                        {creating ? "Creating..." : "Create Case Worker"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>


            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Reset Password</DialogTitle>
                  <CardDescription>
                    Set a new password for <b>{userToReset?.name}</b>.
                  </CardDescription>
                </DialogHeader>
                <form onSubmit={handleResetPassword} className="space-y-4 mt-2">
                  <div>
                    <Label htmlFor="new-pass">New Password</Label>
                    <Input
                      id="new-pass"
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="mt-1.5"
                      disabled={otpRequired}
                    />
                  </div>
                  {otpRequired && (
                    <div>
                      <Label htmlFor="otp-pass">Verification Code</Label>
                      <Input id="otp-pass" inputMode="numeric" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Enter 6-digit code" className="mt-1.5" />
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => { setResetDialogOpen(false); setOtpRequired(false); setOtpCode(""); }}>Cancel</Button>
                    <Button type="submit" disabled={!resetPassword || (otpRequired && otpCode.length !== 6)}>{otpRequired ? "Verify & Update" : "Update Password"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Change Email</DialogTitle>
                  <CardDescription>Update email address for <b>{userToEditEmail?.name}</b>.</CardDescription>
                </DialogHeader>
                <form onSubmit={handleChangeEmail} className="space-y-4 mt-2">
                  <div>
                    <Label htmlFor="new-email">New Email</Label>
                    <Input id="new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Enter new email" className="mt-1.5" disabled={otpRequired} />
                  </div>
                  {otpRequired && (
                    <div>
                      <Label htmlFor="otp-email">Verification Code</Label>
                      <Input id="otp-email" inputMode="numeric" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Enter 6-digit code" className="mt-1.5" />
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => { setEmailDialogOpen(false); setOtpRequired(false); setOtpCode(""); }}>Cancel</Button>
                    <Button type="submit" disabled={!newEmail || (otpRequired && otpCode.length !== 6)}>{otpRequired ? "Verify & Update" : "Update Email"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div >
      </header >

      {/* Main Content */}
      < main className="max-w-4xl mx-auto px-6 py-8" >
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-['Manrope']">All Case Workers</CardTitle>
            <CardDescription>{users.length} case workers total</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {users.map((user) => {
                const RoleIcon = roleIcons[user.role] || User;
                const isAdmin = currentUser?.role === "admin";
                const isSelf = user.id === currentUser?.id;
                const canManage = isSuperAdmin || (isAdmin && (isSelf || user.role === "caseworker" || user.role === "user"));
                const canDelete = !isSelf && user.role !== "super_admin" &&
                  (isSuperAdmin || (isAdmin && (user.role === "caseworker" || user.role === "user")));

                return (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50/50"
                    data-testid={`user-row-${user.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center">
                        <RoleIcon className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{user.name}</div>
                        <div className="text-sm text-slate-500">{user.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        variant="outline"
                        className={`capitalize rounded-sm ${roleColors[user.role]}`}
                      >
                        {(user.role === "caseworker" || user.role === "user") ? "case worker" : user.role.replace("_", " ")}
                      </Badge>
                      {canManage && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEmailDialog(user)} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm" title="Change Email" aria-label={`Change email for ${user.name}`}>
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetDialog(user)}
                            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm"
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {canDelete && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm"
                            data-testid={`delete-user-${user.id}`}
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Permissions Info */}
        <div className="mt-6 p-4 bg-slate-100 rounded-sm text-sm text-slate-600">
          <h4 className="font-medium text-slate-800 mb-2">Role Permissions</h4>
          <ul className="space-y-1">
            <li><span className="font-medium">Super Admin:</span> Full access, can create admins, configure AI settings</li>
            <li><span className="font-medium">Admin:</span> Access all assessments, can create case workers</li>
            <li><span className="font-medium">Case Worker:</span> Can only access their own assessments</li>
          </ul>
        </div>
      </main >
    </div >
  );
}
